import { Action, Dispatch } from "redux";
import { AxiosError, AxiosResponse } from "axios";
import { notification } from "antd";
import { Key } from "react";

import { GET_OFFER_RESPONSES, GET_OFFER_RESPONSE_BY_ID } from "./types";
import { ApiService } from "../../../services";
import {
  NavigationPath,
  downloadBlobFile,
  mapDataRows,
  history,
  showErrorNotification,
} from "../../../utils";
import { ResponseDetailsFormType } from "../../../models";

export const getOfferResponses = () => async (dispatch: Dispatch<Action>) => {
  ApiService.get("/offer-response")
    .then((res: AxiosResponse) => {
      const { status, data } = res;
      if (status !== 200) {
        showErrorNotification("Učitavanje ponuda nije uspjelo");
        return;
      }

      const offerResponses = mapDataRows(data.rows);

      dispatch({
        type: GET_OFFER_RESPONSES,
        payload: offerResponses,
      });
    })
    .catch((err: AxiosError) => {
      showErrorNotification("Učitavanje ponuda nije uspjelo", err);
    });
};

export const deleteOfferResponse =
  (ids: Key[]) => async (dispatch: Dispatch<Action>) => {
    ApiService.delete(`/offer-response/`, {
      data: { ids },
    })
      .then((res: AxiosResponse) => {
        const { status } = res;

        if (status !== 204) {
          showErrorNotification("Brisanje odgovora nije uspjelo");
          return;
        }
        notification.success({
          message:
            ids.length === 1
              ? "Odgovor na zahtjev je obrisan."
              : "Odgovori na zahtjeve su obrisani.",
        });
        getOfferResponses()(dispatch);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Brisanje odgovora nije uspjelo", err);
      });
  };

export const deleteSingleOfferResponse =
  (ids: Key[]) => async (dispatch: Dispatch<Action>) => {
    ApiService.delete(`/offer-response/`, {
      data: { ids },
    })
      .then((res: AxiosResponse) => {
        const { status } = res;

        if (status !== 204) {
          showErrorNotification("Brisanje ponude nije uspjelo");
          return;
        }
        notification.success({
          message:
            ids.length === 1 ? "Ponuda je obrisana." : "Ponude su obrisane.",
        });
        getOfferResponses()(dispatch);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Brisanje ponude nije uspjelo", err);
      });
  };

export const exportExcel = () => async (dispatch: Dispatch<Action>) => {
  ApiService.get("/offer-response/export-xls", {
    responseType: "blob",
  })
    .then((res: AxiosResponse) => {
      const { status } = res;

      if (status !== 200) {
        showErrorNotification("Izvoz u Excel nije uspio");
        return;
      }

      const blob = new Blob([res.data], {
        type: res.headers["content-type"],
      });
      downloadBlobFile(blob, "ponude.xlsx");
    })
    .catch((err: AxiosError) => {
      showErrorNotification("Izvoz u Excel nije uspio", err);
    });
};

export const exportPdf =
  (offerRequestId: string) => async (dispatch: Dispatch<Action>) => {
    ApiService.get(`/offer-response/${offerRequestId}/export-all`, {
      responseType: "blob",
    })
      .then((res: AxiosResponse) => {
        const { status } = res;

        if (status !== 200) {
          showErrorNotification("Izvoz u PDF nije uspio");
          return;
        }

        const blob = new Blob([res.data], {
          type: res.headers["content-type"],
        });
        downloadBlobFile(blob, "sve_ponude.pdf");
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Izvoz u PDF nije uspio", err);
      });
  };

export const getOfferResponseById =
  (id: string) => async (dispatch: Dispatch<Action>) => {
    ApiService.get(`/offer-response/${id}`)
      .then((res: AxiosResponse) => {
        const { status, data } = res;
        if (status !== 200) {
          showErrorNotification("Učitavanje ponude nije uspjelo");
          return;
        }

        dispatch({
          type: GET_OFFER_RESPONSE_BY_ID,
          payload: data,
        });
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Učitavanje ponude nije uspjelo", err);
      });
  };

export const rateOfferResponse =
  (id: string, correct: boolean, incorrectReason: string | null = null) =>
  async (dispatch: Dispatch<Action>) => {
    ApiService.put(`/offer-response/${id}`, { correct, incorrectReason })
      .then((res: AxiosResponse) => {
        const { status } = res;
        if (status !== 200) {
          showErrorNotification("Ocjenjivanje ponude nije uspjelo");
          return;
        }
        notification.success({
          message: "Ponuda je ocijenjena.",
        });
        getOfferResponseById(id)(dispatch);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Ocjenjivanje ponude nije uspjelo", err);
      });
  };

export const updateOfferResponse =
  (id: string, responseDetails: ResponseDetailsFormType) =>
  async (dispatch: Dispatch<Action>) => {
    ApiService.put(`/offer-response/${id}`, responseDetails)
      .then((res: AxiosResponse) => {
        const { status } = res;
        if (status !== 200) {
          showErrorNotification("Spremanje podataka ponude nije uspjelo");
          return;
        }
        notification.success({
          message: "Podaci ponude su sačuvani.",
        });
        history.navigate(NavigationPath.OFFER_RESPONSES_ROUTE);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Spremanje podataka ponude nije uspjelo", err);
      });
  };

export const generateOrder =
  (id: string, filename: string) => async (dispatch: Dispatch<Action>) => {
    ApiService.get(`/offer-response/${id}/generate-order`, {
      responseType: "blob",
    })
      .then((res: AxiosResponse) => {
        const blob = new Blob([res.data]);
        downloadBlobFile(blob, filename);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Generisanje narudžbe nije uspjelo", err);
      });
  };

export const generateInvoice =
  (id: string) => async (dispatch: Dispatch<Action>) => {
    ApiService.get(`/offer-response/${id}/generate-invoice`, {
      responseType: "blob",
    })
      .then((res: AxiosResponse) => {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"],
        });
        downloadBlobFile(blob, "predracun.pdf");
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Generisanje predračuna nije uspjelo", err);
      });
  };

export const downloadFile =
  (id: string, filename: string) => async (dispatch: Dispatch<Action>) => {
    ApiService.get(`offer-response/${id}/file-download`, {
      responseType: "blob",
    })
      .then((res) => {
        const blob = new Blob([res.data]);
        downloadBlobFile(blob, filename);
      })
      .catch((err: AxiosError) => {
        showErrorNotification("Preuzimanje fajla nije uspjelo", err);
      });
  };
